import React, { createContext, useContext, useEffect, useState, useCallback } from 'react'
import { supabase, signOut as supabaseSignOut, getAccessToken } from '../lib/supabase'

const AuthContext = createContext({})

// Tier limits for display
const TIER_LIMITS = {
  anonymous: { questions: 5, fullAnalysis: 2, hasOdds: false },
  free: { questions: 20, fullAnalysis: 5, hasOdds: false },
  pro: { questions: 100, fullAnalysis: 30, hasOdds: true },
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [session, setSession] = useState(null)
  const [loading, setLoading] = useState(true)
  const [subscription, setSubscription] = useState(null)
  const [usage, setUsage] = useState(null)

  // Fetch subscription status from Stripe via analyzer
  const fetchSubscription = useCallback(async (userId) => {
    if (!userId) {
      setSubscription(null)
      setUsage(null)
      return
    }
    
    try {
      // Get access token for API call
      const token = await getAccessToken()
      if (!token) {
        setSubscription({ status: 'free', plan: 'free' })
        setUsage({ questions_used: 0, full_analysis_used: 0 })
        return
      }
      
      // Check subscription via analyzer API (which checks Stripe)
      const analyzerUrl = import.meta.env.VITE_ANALYZER_URL || 'https://orbglide-analyzer.orbglide.workers.dev'
      const res = await fetch(`${analyzerUrl}/api/subscription`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })
      
      if (res.ok) {
        const data = await res.json()
        setSubscription({
          status: data.isPro ? 'pro' : 'free',
          plan: data.isPro ? 'pro' : 'free',
        })
      } else {
        setSubscription({ status: 'free', plan: 'free' })
      }
      
      // Usage is tracked server-side, just set defaults for display
      setUsage({ questions_used: 0, full_analysis_used: 0 })
    } catch (err) {
      console.log('Subscription fetch error:', err.message)
      setSubscription({ status: 'free', plan: 'free' })
      setUsage({ questions_used: 0, full_analysis_used: 0 })
    }
  }, [])

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      setLoading(false)
      if (session?.user) {
        fetchSubscription(session.user.id)
      }
    })

    // Listen for auth changes
    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session)
        setUser(session?.user ?? null)
        setLoading(false)
        if (session?.user) {
          fetchSubscription(session.user.id)
        } else {
          setSubscription(null)
          setUsage(null)
        }
      }
    )

    return () => authSub.unsubscribe()
  }, [fetchSubscription])

  const signOut = async () => {
    await supabaseSignOut()
    setUser(null)
    setSession(null)
  }

  // Compute tier
  // Dev mode: check localStorage for DEV_PRO_MODE to test Pro features
  const devProMode = typeof window !== 'undefined' && localStorage.getItem('DEV_PRO_MODE') === 'true'
  const isPro = devProMode || subscription?.status === 'pro'
  
  const tier = isPro ? 'pro' : (user ? 'free' : 'anonymous')
  const limits = TIER_LIMITS[tier]

  const value = {
    user,
    session,
    loading,
    signOut,
    isAuthenticated: !!user,
    getAccessToken,
    // Subscription info
    subscription,
    usage,
    tier,
    limits,
    isPro,
    refreshSubscription: () => user && fetchSubscription(user.id),
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
